import { useEffect, useState, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link } from 'react-router-dom'
import { FaEye, FaSearch } from 'react-icons/fa'
import { FiRefreshCw } from 'react-icons/fi'
import api from '../../../libs/axios'
import { useDebounce } from '../../../hooks/useDebounce'

interface Site {
  id: number
  site_id?: string
  site_name?: string
  reference_number?: string
  survey_workflow_status: string
  site_priority?: string
  project?: { project_name: string }
  state_circle?: { state_name: string }
  site_engineer?: { first_name: string; last_name: string; username: string }
  report_designer?: { first_name: string; last_name: string; username: string }
}

const STATUS_COLORS: Record<string, string> = {
  SITE_CREATED: 'bg-gray-100 text-gray-700',
  ENGINEER_ASSIGNED: 'bg-blue-100 text-blue-700',
  FIELD_WORK_IN_PROGRESS: 'bg-yellow-100 text-yellow-700',
  DATA_UPLOADED: 'bg-orange-100 text-orange-700',
  FIELD_WORK_COMPLETED: 'bg-indigo-100 text-indigo-700',
  TA_REVIEW_PENDING: 'bg-purple-100 text-purple-700',
  TA_REVIEW_COMPLETED: 'bg-violet-100 text-violet-700',
  REPORT_MAKER_ASSIGNED: 'bg-cyan-100 text-cyan-700',
  REPORT_IN_PROGRESS: 'bg-sky-100 text-sky-700',
  REPORT_COMPLETED: 'bg-teal-100 text-teal-700',
  VALIDATION_COMPLETED: 'bg-emerald-100 text-emerald-700',
  REPORT_FINALIZED: 'bg-lime-100 text-lime-700',
  REPORT_SENT_TO_CLIENT: 'bg-green-100 text-green-700',
}

const statusLabel = (s: string) => s?.replace(/_/g, ' ') ?? '—'

const STATUSES = [
  { value: '', label: 'All Statuses' },
  { value: 'SITE_CREATED', label: 'Site Created' },
  { value: 'ENGINEER_ASSIGNED', label: 'Engineer Assigned' },
  { value: 'FIELD_WORK_IN_PROGRESS', label: 'Field Work In Progress' },
  { value: 'FIELD_WORK_COMPLETED', label: 'Field Work Completed' },
  { value: 'TA_REVIEW_PENDING', label: 'TA Review Pending' },
  { value: 'TA_REVIEW_COMPLETED', label: 'TA Review Completed' },
  { value: 'REPORT_MAKER_ASSIGNED', label: 'Report Maker Assigned' },
  { value: 'REPORT_IN_PROGRESS', label: 'Report In Progress' },
  { value: 'REPORT_COMPLETED', label: 'Report Completed' },
  { value: 'VALIDATION_COMPLETED', label: 'Validation Completed' },
  { value: 'REPORT_FINALIZED', label: 'Report Finalized' },
  { value: 'REPORT_SENT_TO_CLIENT', label: 'Report Sent to Client' },
]

export default function VendorPortalSites() {
  const [sites, setSites] = useState<Site[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [loading, setLoading] = useState(true)
  const [searchInput, setSearchInput] = useState('')
  const [statusFilter, setStatusFilter] = useState('')

  const debouncedSearch = useDebounce(searchInput, 400)

  const fetchSites = useCallback(() => {
    setLoading(true)
    const params: any = { page, limit: 20 }
    if (statusFilter) params.survey_workflow_status = statusFilter
    if (debouncedSearch) params.search = debouncedSearch
    api.get('/vendor-portal/sites', { params })
      .then(r => {
        setSites(r.data.data?.rows ?? [])
        setTotal(r.data.data?.count ?? 0)
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [page, statusFilter, debouncedSearch])

  useEffect(() => { fetchSites() }, [fetchSites])

  const clearSearch = () => setSearchInput('')

  return (
    <>
      <Helmet><title>Vendor Portal – Sites</title></Helmet>
      <div className="space-y-4">

        {/* Header card */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-gray-900">Sites</h2>
              <p className="text-sm text-gray-600 mt-1">{total} sites across your vendor team</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-center">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search site name / ref / site ID..."
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
                />
                <FaSearch className="absolute left-3 top-3 text-gray-400 w-4 h-4 pointer-events-none" />
                {searchInput && (
                  <button onClick={clearSearch} className="absolute right-2 top-2.5 text-gray-400 hover:text-gray-600 text-xs">✕</button>
                )}
              </div>
              <select
                value={statusFilter}
                onChange={e => { setStatusFilter(e.target.value); setPage(1) }}
                className="px-4 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm bg-white"
              >
                {STATUSES.map(s => (
                  <option key={s.value} value={s.value}>{s.label}</option>
                ))}
              </select>
              <button
                onClick={fetchSites}
                className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 text-gray-700 rounded hover:bg-gray-50 transition-colors text-sm font-medium"
              >
                <FiRefreshCw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Table card */}
        <div className="bg-white rounded shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Site</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Project</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Circle</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Engineer</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Report Maker</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  [...Array(8)].map((_, i) => (
                    <tr key={i}>
                      {[...Array(8)].map((_, j) => (
                        <td key={j} className="px-4 py-3">
                          <div className="h-4 bg-gray-100 rounded animate-pulse" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : sites.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="px-4 py-10 text-center text-gray-400 text-sm">
                      No sites found
                    </td>
                  </tr>
                ) : sites.map(site => (
                  <tr key={site.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900 text-sm">
                      {site.site_name ?? '—'}
                      {site.site_id && <div className="text-xs text-gray-400 mt-0.5">{site.site_id}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{site.reference_number ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{site.project?.project_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-sm">{site.state_circle?.state_name ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {site.site_engineer
                        ? `${site.site_engineer.first_name} ${site.site_engineer.last_name}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3 text-gray-600 text-xs">
                      {site.report_designer
                        ? `${site.report_designer.first_name} ${site.report_designer.last_name}`
                        : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[site.survey_workflow_status] ?? 'bg-gray-100 text-gray-600'}`}>
                        {statusLabel(site.survey_workflow_status)}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        to={`/vendor-portal/sites/${site.id}`}
                        className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 font-medium text-xs"
                      >
                        <FaEye className="w-3.5 h-3.5" /> View
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {total > 20 && (
            <div className="px-4 py-3 border-t border-gray-200 flex items-center justify-between text-sm text-gray-500">
              <span>Showing {(page - 1) * 20 + 1}–{Math.min(page * 20, total)} of {total}</span>
              <div className="flex gap-2">
                <button
                  disabled={page === 1}
                  onClick={() => setPage(p => p - 1)}
                  className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
                >
                  Prev
                </button>
                <button
                  disabled={page * 20 >= total}
                  onClick={() => setPage(p => p + 1)}
                  className="px-3 py-1 border border-gray-200 rounded disabled:opacity-40 hover:bg-gray-50"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
